/**
 * Short-form catalogue — the published vertical clips, newest first (the order
 * of the channel's /shorts tab on 2026-09-04).
 *
 * youtubeUrl: re-synced 2026-09-04 from the channel's /shorts tab. The channel
 * had re-uploaded every short under a new id, so the earlier ledger ids all
 * returned "Video unavailable"; the 16 originals were re-matched by CPT code and
 * the 21 newer shorts (imaging / X-ray / nerve block) were added.
 * mp4: a vertical MP4 on our CDN in the CURRENT skin, used for the muted
 * autoplay preview in the Classroom feed. None exist yet, so every card shows
 * `poster` (YouTube's vertical thumbnail — Master set custom ones 2026-09-05).
 * legacyMp4: the 16 old TikTok-format renders. Retired from the UI 2026-09-05
 * (Master: "not the old tiktok format, only the new skinned ones"); kept here
 * only so the files can be found.
 * tiktokUrl is null until the TikTok post URL is pasted in (posted by hand, so
 * we don't know the video ids) — the TikTok button per card renders only once
 * it's filled. To re-sync: read the ids off youtube.com/@americanhospitalprices/shorts
 * and match on the "(CPT xxxxx)" suffix in each title.
 */
export interface ShortClip {
  id: string; code: string; title: string; payoff: string;
  /** Vertical MP4 on our CDN in the current skin (muted autoplay preview); null when only YouTube has it. */
  mp4: string | null;
  /** Old TikTok-format render on the CDN — not shown anywhere, see note above. */
  legacyMp4?: string;
  /** YouTube's vertical thumbnail for the short (i.ytimg.com/vi/<id>/oar2.jpg). */
  poster: string;
  youtubeUrl: string; tiktokUrl: string | null;
}

export const SHORTS_PLAYLIST = "https://www.youtube.com/playlist?list=PLRDei3uMCLIA";

/**
 * In-place YouTube playback in the Classroom Shorts feed. Requires "Allow
 * embedding" ON for the shorts in YouTube Studio (Content → select → Edit →
 * Embedding → Allow) — Master enabled it channel-wide 2026-09-05. If a short
 * ever shows "Playback on other websites has been disabled", set this false and
 * "Watch on YouTube" opens the short on youtube.com in a new tab instead (also
 * a counted view).
 */
export const SHORTS_EMBED_ALLOWED = true;

export const SHORTS: ShortClip[] = [
  {
    "id": "yt_64435",
    "code": "64435",
    "title": "A paracervical nerve block: $240 or $4,118.",
    "payoff": "$240 → $4,118",
    "mp4": null,
    "poster": "https://i.ytimg.com/vi/U3CLFfEuz1w/oar2.jpg",
    "youtubeUrl": "https://www.youtube.com/shorts/U3CLFfEuz1w",
    "tiktokUrl": null
  },
  {
    "id": "yt_76856",
    "code": "76856",
    "title": "A pelvic ultrasound: $420 or $2,357.",
    "payoff": "$420 → $2,357",
    "mp4": null,
    "poster": "https://i.ytimg.com/vi/gKYWJXhYTGk/oar2.jpg",
    "youtubeUrl": "https://www.youtube.com/shorts/gKYWJXhYTGk",
    "tiktokUrl": null
  },
  {
    "id": "yt_76705",
    "code": "76705",
    "title": "A limited abdominal ultrasound: $400 or $2,198.",
    "payoff": "$400 → $2,198",
    "mp4": null,
    "poster": "https://i.ytimg.com/vi/RtItA2Dobdk/oar2.jpg",
    "youtubeUrl": "https://www.youtube.com/shorts/RtItA2Dobdk",
    "tiktokUrl": null
  },
  {
    "id": "yt_70544",
    "code": "70544",
    "title": "A brain MRA scan: $1,326 or $6,514.",
    "payoff": "$1,326 → $6,514",
    "mp4": null,
    "poster": "https://i.ytimg.com/vi/qJeCbSQAiZ4/oar2.jpg",
    "youtubeUrl": "https://www.youtube.com/shorts/qJeCbSQAiZ4",
    "tiktokUrl": null
  },
  {
    "id": "yt_74183",
    "code": "74183",
    "title": "An abdominal MRI: $2,032 or $9,828.",
    "payoff": "$2,032 → $9,828",
    "mp4": null,
    "poster": "https://i.ytimg.com/vi/6kpB2O_HwZA/oar2.jpg",
    "youtubeUrl": "https://www.youtube.com/shorts/6kpB2O_HwZA",
    "tiktokUrl": null
  },
  {
    "id": "yt_72141",
    "code": "72141",
    "title": "A neck MRI: $1,350 or $7,324.",
    "payoff": "$1,350 → $7,324",
    "mp4": null,
    "poster": "https://i.ytimg.com/vi/kLRAICWugUQ/oar2.jpg",
    "youtubeUrl": "https://www.youtube.com/shorts/kLRAICWugUQ",
    "tiktokUrl": null
  },
  {
    "id": "yt_72158",
    "code": "72158",
    "title": "A lower back MRI with contrast: $2,051 or $10,376.",
    "payoff": "$2,051 → $10,376",
    "mp4": null,
    "poster": "https://i.ytimg.com/vi/9w1JLRcgFSI/oar2.jpg",
    "youtubeUrl": "https://www.youtube.com/shorts/9w1JLRcgFSI",
    "tiktokUrl": null
  },
  {
    "id": "yt_72131",
    "code": "72131",
    "title": "A lower back CT scan: $850 or $5,947.",
    "payoff": "$850 → $5,947",
    "mp4": null,
    "poster": "https://i.ytimg.com/vi/hSJ433Ev1uY/oar2.jpg",
    "youtubeUrl": "https://www.youtube.com/shorts/hSJ433Ev1uY",
    "tiktokUrl": null
  },
  {
    "id": "yt_70496",
    "code": "70496",
    "title": "A head CT angiogram: $1,199 or $7,418.",
    "payoff": "$1,199 → $7,418",
    "mp4": null,
    "poster": "https://i.ytimg.com/vi/MI_KLX1B1w0/oar2.jpg",
    "youtubeUrl": "https://www.youtube.com/shorts/MI_KLX1B1w0",
    "tiktokUrl": null
  },
  {
    "id": "yt_71275",
    "code": "71275",
    "title": "A chest CT angiogram: $1,203 or $7,781.",
    "payoff": "$1,203 → $7,781",
    "mp4": null,
    "poster": "https://i.ytimg.com/vi/AV2mzoJXM9s/oar2.jpg",
    "youtubeUrl": "https://www.youtube.com/shorts/AV2mzoJXM9s",
    "tiktokUrl": null
  },
  {
    "id": "yt_70470",
    "code": "70470",
    "title": "A brain CT with contrast: $1,145 or $7,308.",
    "payoff": "$1,145 → $7,308",
    "mp4": null,
    "poster": "https://i.ytimg.com/vi/EBcbm2TCyDw/oar2.jpg",
    "youtubeUrl": "https://www.youtube.com/shorts/EBcbm2TCyDw",
    "tiktokUrl": null
  },
  {
    "id": "yt_71250",
    "code": "71250",
    "title": "A chest CT scan: $818 or $5,432.",
    "payoff": "$818 → $5,432",
    "mp4": null,
    "poster": "https://i.ytimg.com/vi/Zb_oiuPS80E/oar2.jpg",
    "youtubeUrl": "https://www.youtube.com/shorts/Zb_oiuPS80E",
    "tiktokUrl": null
  },
  {
    "id": "yt_74177",
    "code": "74177",
    "title": "An abdominal CT scan: $1,566 or $11,998.",
    "payoff": "$1,566 → $11,998",
    "mp4": null,
    "poster": "https://i.ytimg.com/vi/5JAMeTmumL8/oar2.jpg",
    "youtubeUrl": "https://www.youtube.com/shorts/5JAMeTmumL8",
    "tiktokUrl": null
  },
  {
    "id": "yt_72050",
    "code": "72050",
    "title": "A neck X-ray: $270 or $1,749.",
    "payoff": "$270 → $1,749",
    "mp4": null,
    "poster": "https://i.ytimg.com/vi/pTnPdQyxrqI/oar2.jpg",
    "youtubeUrl": "https://www.youtube.com/shorts/pTnPdQyxrqI",
    "tiktokUrl": null
  },
  {
    "id": "yt_72100",
    "code": "72100",
    "title": "A lower back X-ray: $235 or $1,345.",
    "payoff": "$235 → $1,345",
    "mp4": null,
    "poster": "https://i.ytimg.com/vi/jQtZG6VPP2A/oar2.jpg",
    "youtubeUrl": "https://www.youtube.com/shorts/jQtZG6VPP2A",
    "tiktokUrl": null
  },
  {
    "id": "yt_71046",
    "code": "71046",
    "title": "A chest X-ray: $200 or $1,054.",
    "payoff": "$200 → $1,054",
    "mp4": null,
    "poster": "https://i.ytimg.com/vi/ru6braFmPmc/oar2.jpg",
    "youtubeUrl": "https://www.youtube.com/shorts/ru6braFmPmc",
    "tiktokUrl": null
  },
  {
    "id": "yt_73030",
    "code": "73030",
    "title": "A shoulder X-ray: $213 or $1,542.",
    "payoff": "$213 → $1,542",
    "mp4": null,
    "poster": "https://i.ytimg.com/vi/DGiPYrUfM40/oar2.jpg",
    "youtubeUrl": "https://www.youtube.com/shorts/DGiPYrUfM40",
    "tiktokUrl": null
  },
  {
    "id": "yt_73560",
    "code": "73560",
    "title": "A knee X-ray: $187 or $1,258.",
    "payoff": "$187 → $1,258",
    "mp4": null,
    "poster": "https://i.ytimg.com/vi/perktB2HNjM/oar2.jpg",
    "youtubeUrl": "https://www.youtube.com/shorts/perktB2HNjM",
    "tiktokUrl": null
  },
  {
    "id": "yt_73110",
    "code": "73110",
    "title": "A wrist X-ray: $208 or $1,435.",
    "payoff": "$208 → $1,435",
    "mp4": null,
    "poster": "https://i.ytimg.com/vi/ntDy5bf_Vvc/oar2.jpg",
    "youtubeUrl": "https://www.youtube.com/shorts/ntDy5bf_Vvc",
    "tiktokUrl": null
  },
  {
    "id": "yt_73130",
    "code": "73130",
    "title": "A hand X-ray: $202 or $1,449.",
    "payoff": "$202 → $1,449",
    "mp4": null,
    "poster": "https://i.ytimg.com/vi/6OS7TSYUtIM/oar2.jpg",
    "youtubeUrl": "https://www.youtube.com/shorts/6OS7TSYUtIM",
    "tiktokUrl": null
  },
  {
    "id": "yt_73610",
    "code": "73610",
    "title": "An ankle X-ray: $209 or $1,474.",
    "payoff": "$209 → $1,474",
    "mp4": null,
    "poster": "https://i.ytimg.com/vi/W4cKprMqNOE/oar2.jpg",
    "youtubeUrl": "https://www.youtube.com/shorts/W4cKprMqNOE",
    "tiktokUrl": null
  },
  {
    "id": "04_fingerstick",
    "code": "36416",
    "title": "A finger prick. $32 — or $17,942.",
    "payoff": "$32 → $122",
    "mp4": null,
    "legacyMp4": "https://static.americanhealthprices.com/kit/AHP_yt_04_fingerstick.mp4",
    "poster": "https://i.ytimg.com/vi/s3nPiSw3Ckc/oar2.jpg",
    "youtubeUrl": "https://www.youtube.com/shorts/s3nPiSw3Ckc",
    "tiktokUrl": null
  },
  {
    "id": "12_er3",
    "code": "99283",
    "title": "An ER visit charge, before anyone treats you.",
    "payoff": "$412 → $2,917",
    "mp4": null,
    "legacyMp4": "https://static.americanhealthprices.com/kit/AHP_yt_12_er3.mp4",
    "poster": "https://i.ytimg.com/vi/9HAAyhmmLK0/oar2.jpg",
    "youtubeUrl": "https://www.youtube.com/shorts/9HAAyhmmLK0",
    "tiktokUrl": null
  },
  {
    "id": "20_kneescope",
    "code": "29881",
    "title": "Knee surgery: $1,445 or $28,462.",
    "payoff": "$1,445 → $28,462",
    "mp4": null,
    "legacyMp4": "https://static.americanhealthprices.com/kit/AHP_yt_20_kneescope.mp4",
    "poster": "https://i.ytimg.com/vi/G9S7_gXX78k/oar2.jpg",
    "youtubeUrl": "https://www.youtube.com/shorts/G9S7_gXX78k",
    "tiktokUrl": null
  },
  {
    "id": "18_colonoscopy",
    "code": "45378",
    "title": "A colonoscopy: $864 or $8,278.",
    "payoff": "$864 → $8,278",
    "mp4": null,
    "legacyMp4": "https://static.americanhealthprices.com/kit/AHP_yt_18_colonoscopy.mp4",
    "poster": "https://i.ytimg.com/vi/GAUr1PTLS5k/oar2.jpg",
    "youtubeUrl": "https://www.youtube.com/shorts/GAUr1PTLS5k",
    "tiktokUrl": null
  },
  {
    "id": "15_mriback",
    "code": "72148",
    "title": "An MRI of your lower back: $1,358 or $7,441.",
    "payoff": "$1,358 → $7,441",
    "mp4": null,
    "legacyMp4": "https://static.americanhealthprices.com/kit/AHP_yt_15_mriback.mp4",
    "poster": "https://i.ytimg.com/vi/_fRxZ0MAxnQ/oar2.jpg",
    "youtubeUrl": "https://www.youtube.com/shorts/_fRxZ0MAxnQ",
    "tiktokUrl": null
  },
  {
    "id": "16_mribrain",
    "code": "70553",
    "title": "A brain MRI: $2,075 or $10,245.",
    "payoff": "$2,075 → $10,245",
    "mp4": null,
    "legacyMp4": "https://static.americanhealthprices.com/kit/AHP_yt_16_mribrain.mp4",
    "poster": "https://i.ytimg.com/vi/grko83AO1Zs/oar2.jpg",
    "youtubeUrl": "https://www.youtube.com/shorts/grko83AO1Zs",
    "tiktokUrl": null
  },
  {
    "id": "14_ctscan",
    "code": "70450",
    "title": "A head CT: $823 or $5,224.",
    "payoff": "$823 → $5,224",
    "mp4": null,
    "legacyMp4": "https://static.americanhealthprices.com/kit/AHP_yt_14_ctscan.mp4",
    "poster": "https://i.ytimg.com/vi/AEp2bMAAchc/oar2.jpg",
    "youtubeUrl": "https://www.youtube.com/shorts/AEp2bMAAchc",
    "tiktokUrl": null
  },
  {
    "id": "11_stitches",
    "code": "12001",
    "title": "Stitches for a small cut: $245 or $1,743.",
    "payoff": "$555 typical",
    "mp4": null,
    "legacyMp4": "https://static.americanhealthprices.com/kit/AHP_yt_11_stitches.mp4",
    "poster": "https://i.ytimg.com/vi/Kqap4jt06ls/oar2.jpg",
    "youtubeUrl": "https://www.youtube.com/shorts/Kqap4jt06ls",
    "tiktokUrl": null
  },
  {
    "id": "19_endoscopy",
    "code": "43239",
    "title": "An upper endoscopy: $923 or $8,314.",
    "payoff": "$923 → $8,314",
    "mp4": null,
    "legacyMp4": "https://static.americanhealthprices.com/kit/AHP_yt_19_endoscopy.mp4",
    "poster": "https://i.ytimg.com/vi/5nhfcs2h9zg/oar2.jpg",
    "youtubeUrl": "https://www.youtube.com/shorts/5nhfcs2h9zg",
    "tiktokUrl": null
  },
  {
    "id": "17_ultrasound",
    "code": "76700",
    "title": "An abdominal ultrasound: $472 or $2,811.",
    "payoff": "$472 → $2,811",
    "mp4": null,
    "legacyMp4": "https://static.americanhealthprices.com/kit/AHP_yt_17_ultrasound.mp4",
    "poster": "https://i.ytimg.com/vi/WgnFXl4HCf4/oar2.jpg",
    "youtubeUrl": "https://www.youtube.com/shorts/WgnFXl4HCf4",
    "tiktokUrl": null
  },
  {
    "id": "09_ekg",
    "code": "93000",
    "title": "An EKG: 12 stickers, 5 minutes, $434?",
    "payoff": "$114 → $295",
    "mp4": null,
    "legacyMp4": "https://static.americanhealthprices.com/kit/AHP_yt_09_ekg.mp4",
    "poster": "https://i.ytimg.com/vi/l4gp6GIl-Jg/oar2.jpg",
    "youtubeUrl": "https://www.youtube.com/shorts/l4gp6GIl-Jg",
    "tiktokUrl": null
  },
  {
    "id": "06_cbc",
    "code": "85025",
    "title": "A blood count: $37 or $305, same tube.",
    "payoff": "$37 → $305",
    "mp4": null,
    "legacyMp4": "https://static.americanhealthprices.com/kit/AHP_yt_06_cbc.mp4",
    "poster": "https://i.ytimg.com/vi/hf901UBkZNI/oar2.jpg",
    "youtubeUrl": "https://www.youtube.com/shorts/hf901UBkZNI",
    "tiktokUrl": null
  },
  {
    "id": "08_glucose",
    "code": "82947",
    "title": "A blood sugar test: $23 or $229.",
    "payoff": "$23 → $229",
    "mp4": null,
    "legacyMp4": "https://static.americanhealthprices.com/kit/AHP_yt_08_glucose.mp4",
    "poster": "https://i.ytimg.com/vi/jI6H43InEZI/oar2.jpg",
    "youtubeUrl": "https://www.youtube.com/shorts/jI6H43InEZI",
    "tiktokUrl": null
  },
  {
    "id": "07_metabolic",
    "code": "80053",
    "title": "Fourteen results, one tube: $55 or $917.",
    "payoff": "$261 typical",
    "mp4": null,
    "legacyMp4": "https://static.americanhealthprices.com/kit/AHP_yt_07_metabolic.mp4",
    "poster": "https://i.ytimg.com/vi/DkOg6tdxKOY/oar2.jpg",
    "youtubeUrl": "https://www.youtube.com/shorts/DkOg6tdxKOY",
    "tiktokUrl": null
  },
  {
    "id": "10_thyroid",
    "code": "84443",
    "title": "A thyroid test: $75 or $545.",
    "payoff": "$75 → $545",
    "mp4": null,
    "legacyMp4": "https://static.americanhealthprices.com/kit/AHP_yt_10_thyroid.mp4",
    "poster": "https://i.ytimg.com/vi/6hVrdMRBVu4/oar2.jpg",
    "youtubeUrl": "https://www.youtube.com/shorts/6hVrdMRBVu4",
    "tiktokUrl": null
  },
  {
    "id": "05_urinalysis",
    "code": "81001",
    "title": "A urine test: $20 or $291.",
    "payoff": "$20 → $291",
    "mp4": null,
    "legacyMp4": "https://static.americanhealthprices.com/kit/AHP_yt_05_urinalysis.mp4",
    "poster": "https://i.ytimg.com/vi/GR2tqbIOCYA/oar2.jpg",
    "youtubeUrl": "https://www.youtube.com/shorts/GR2tqbIOCYA",
    "tiktokUrl": null
  }
];
